import { getFullUrl, getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';

import type { SessionReplayOptions } from '@amplitude/session-replay-browser';

import * as browserHelper from 'usage-tracker-core/client/browserHelper';

import * as srConstants from './constants';
import type { SROptions } from './types';

export const getSessionReplayConfig = (
  options: SROptions = {},
  deviceId: string,
  sessionId: number
): SessionReplayOptions => {
  // the config endpoint always runs on the app load balancer within the SessionReplay service
  const configOrigin = getFullUrl('app');

  // the intake origin must run on the same subdomain where the app is running, due to CORS.
  const intakeOrigin = getFullUrlWithCurrentSubDomain();

  // the `app` load balancer always requires /api for most of services running against app LB
  const intakePrefix = /hubspot(qa)?\.com$/.test(intakeOrigin) ? '/api' : '';

  const {
    // The sample rate of the session replay events. From 0-1 (1 = 100%, 0 = 0%)
    sampleRate = srConstants.DEFAULT_SAMPLE_RATE,
    // If you want to opt-out programmatically from the session replays
    // Opt out by default on non-deployed (local) environments to avoid 404s and
    // rrWeb/CORS noise; the debug block below overrides this when debug is enabled.
    optOut = browserHelper.isLocalDeployment,
    // Which elements to block (remove the elements) for PII reasons
    blockSelectors = [],
    // Which elements to mask with ***** (hide with a placeholder) for PII reasons
    maskSelectors = [],
    // Which elements to unmask (restore original content)
    unmaskSelectors = [],
  } = options;

  const sessionReplaySettings: SessionReplayOptions = {
    // Do compression on idle Browser periods, to reduce impact on user interaction
    performanceConfig: { enabled: true },
    // Force to IndexedDB store by default; Allows us to store temporary metadata and let the Service Worker
    // compress the events and send it to IndexedDB instead of using the browser's JavaScript main thread.
    storeType: 'idb',
    maxPersistedEventsSizeBytes: 1_000_000,
    // We allow Apps to opt-out of Session Replay within their instances when wrapping
    // on `withSessionReplay` method. (Assuming if they want to opt-out) on specific things
    // such as, QA environment, or specific Portals, etc.
    optOut,
    // If no sample-rate is provided, we default to sampling 1% of the sessions
    // @see https://www.docs.developers.amplitude.com/session-replay/sdks/standalone/#sampling-rate
    // Note: When there is no App-specific Sampling Config, it will fallback to the sampling rate used by the SDK
    // Sampling Rates from the Server will only be provided if there is an App-specific sampling rate.
    sampleRate,
    // The HAmplitude Session ID, for all trackers this will be a 1:1 match
    sessionId,
    // The HAmplitude Session ID, for all trackers this will be a 1:1 match
    deviceId,
    // Allows us to mask specific elements from being sent to Amplitude for PII reasons
    // We by default include a bunch of predefined selectors, but Apps should add extra selectors on their own discretion
    privacyConfig: {
      blockSelector:
        // All the elements to be blocked by default on the SDK-level
        srConstants.DEFAULT_PII_SELECTORS.BLOCK.concat(blockSelectors),
      maskSelector:
        // All the elements to be masked by default on the SDK-level
        srConstants.DEFAULT_PII_SELECTORS.MASK.concat(maskSelectors),
      unmaskSelector:
        // All the elements to be unmasked by default on the SDK-level
        srConstants.DEFAULT_PII_SELECTORS.UNMASK.concat(unmaskSelectors),
      // The Conservative Setting by default will mask all text and all form fields
      // including HTML text, user input, links, etc.
      defaultMaskLevel: 'conservative',
    },
    configServerUrl: `${configOrigin}${srConstants.CONFIG_ENDPOINT_URL}`,
    trackServerUrl: `${intakeOrigin}${intakePrefix}${srConstants.TRACK_ENDPOINT_URL}`,
  };

  // If the user requests debugMode, which can be useful for test environments, or debugging its behaviour locally
  // or simply forcing Session Replay to run, we enable debug mode on the Session Replay level
  // This can be useful for observing sessions and many other things.
  if (!browserHelper.isDebugEnabled) {
    return sessionReplaySettings;
  }

  return {
    // Prepend all already configured settings
    ...sessionReplaySettings,
    // Enables Debug Mode on Session Replay-level
    debugMode: true,
    // Log Level 4 = Debug Logs
    // See: `@amplitude/session-replay-browser/index.d.ts#LogLevel`
    logLevel: 4,
    // We force everyone to not be opted out when debug is enabled
    optOut: false,
    // We force the sample rate to be 100% when debug is enabled
    sampleRate: 1,
  };
};
